import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

const originalPositions = [
  { cx: 3.691, cy: 10, opacity: 1 },
  { cx: 10, cy: 0.537, opacity: 1 },
  { cx: 10, cy: 6.846, opacity: 1 },
  { cx: 10, cy: 13.154, opacity: 1 },
  { cx: 10, cy: 3.692, opacity: 1 },
  { cx: 10, cy: 10, opacity: 1 },
  { cx: 10, cy: 16.308, opacity: 1 },
  { cx: 10, cy: 19.463, opacity: 1 },
  { cx: 16.308, cy: 10, opacity: 1 },
  { cx: 0.537, cy: 10, opacity: 1 },
  { cx: 6.846, cy: 10, opacity: 1 },
  { cx: 13.154, cy: 10, opacity: 1 },
  { cx: 19.463, cy: 10, opacity: 1 },
];

const checkmarkPositions = [
  { cx: 3, cy: 10, opacity: 1 },
  { cx: 4.254, cy: 11.254, opacity: 1 },
  { cx: 5.508, cy: 12.508, opacity: 1 },
  { cx: 6.762, cy: 13.762, opacity: 1 },
  { cx: 8.014, cy: 14.982, opacity: 1 },
  { cx: 9.138, cy: 13.61, opacity: 1 },
  { cx: 10.261, cy: 12.237, opacity: 1 },
  { cx: 11.385, cy: 10.863, opacity: 1 },
  { cx: 12.508, cy: 9.49, opacity: 1 },
  { cx: 13.631, cy: 8.117, opacity: 1 },
  { cx: 14.755, cy: 6.744, opacity: 1 },
  { cx: 15.877, cy: 5.373, opacity: 1 },
  { cx: 17, cy: 4, opacity: 1 },
];

const linePositions = [2, 6, 10, 14, 18, 2, 6, 10, 14, 18, 6, 10, 14].map(
  (cx) => ({ cx, cy: 10 }),
);

const crossPositions = [{ cx: 10, cy: 10 }].concat(
  ...[2.2, 4.4, 6.6].map((d) => [
    { cx: 10 - d, cy: 10 - d },
    { cx: 10 + d, cy: 10 - d },
    { cx: 10 - d, cy: 10 + d },
    { cx: 10 + d, cy: 10 + d },
  ]),
);

const resultShapes = {
  success: checkmarkPositions,
  error: crossPositions,
};

export const RESULT_LINE_DURATION = 0.35;
export const RESULT_MOVE_DURATION = 0.6;
const RESULT_SHAPE_DURATION = 0.5;

const PlusSubmitIcon = ({ status = 'idle', ...props }) => {
  const svgRef = useRef(null);
  const groupRef = useRef(null);
  const animation = useRef(null);

  useLayoutEffect(() => {
    const circles = gsap.utils.toArray(
      svgRef.current.querySelectorAll('circle'),
    );

    if (animation.current) {
      animation.current.kill();
    }
    gsap.set(svgRef.current, { x: 0 });

    if (status === 'loading') {
      animation.current = gsap.timeline();
      const radius = 6.5;
      const center = 10;
      const numVisibleDots = 9;
      const arcAngle = 1.6 * Math.PI;

      circles.forEach((circle, i) => {
        if (i < numVisibleDots) {
          const angle = (i / numVisibleDots) * arcAngle;
          const targetX = center + radius * Math.cos(angle);
          const targetY = center + radius * Math.sin(angle);

          animation.current.to(
            circle,
            {
              attr: { cx: targetX, cy: targetY },
              opacity: 1,
              duration: 0.5,
              ease: 'power3.inOut',
            },
            0,
          );
        } else {
          animation.current.to(
            circle,
            {
              opacity: 0,
              duration: 0.4,
              ease: 'power2.out',
            },
            0,
          );
        }
      });

      animation.current.to(
        groupRef.current,
        {
          rotation: 360,
          transformOrigin: 'center center',
          duration: 1,
          ease: 'none',
          repeat: -1,
        },
        '>-0.2',
      );
    } else if (resultShapes[status]) {
      const shape = resultShapes[status];
      animation.current = gsap.timeline();

      const rotation = gsap.getProperty(groupRef.current, 'rotation') || 0;
      animation.current
        .to(
          groupRef.current,
          {
            rotation: Math.ceil(rotation / 360) * 360,
            transformOrigin: 'center center',
            duration: RESULT_LINE_DURATION,
            ease: 'power2.out',
          },
          0,
        )
        .set(groupRef.current, { rotation: 0 }, RESULT_LINE_DURATION);

      const shapeStart = RESULT_LINE_DURATION + RESULT_MOVE_DURATION;
      circles.forEach((circle, i) => {
        animation.current
          .to(
            circle,
            {
              attr: { cx: linePositions[i].cx, cy: linePositions[i].cy },
              opacity: 1,
              duration: RESULT_LINE_DURATION,
              ease: 'power3.inOut',
            },
            0,
          )
          .to(
            circle,
            {
              attr: { cx: shape[i].cx, cy: shape[i].cy },
              opacity: 1,
              duration: RESULT_SHAPE_DURATION,
              ease: 'power3.inOut',
            },
            shapeStart,
          );
      });

      if (status === 'error') {
        animation.current.to(
          svgRef.current,
          {
            keyframes: { x: [0, -2, 2, -1.5, 1.5, 0] },
            duration: 0.4,
            ease: 'none',
          },
          shapeStart + RESULT_SHAPE_DURATION,
        );
      }
    } else {
      animation.current = gsap.timeline();

      circles.forEach((circle, i) => {
        animation.current.to(
          circle,
          {
            attr: { cx: originalPositions[i].cx, cy: originalPositions[i].cy },
            opacity: originalPositions[i].opacity,
            duration: 0.5,
            ease: 'power3.inOut',
          },
          0,
        );
      });

      gsap.set(groupRef.current, { rotation: 0 });
    }

    return () => {
      if (animation.current) {
        animation.current.kill();
      }
    };
  }, [status]);

  return (
    <svg
      ref={svgRef}
      xmlns='http://www.w3.org/2000/svg'
      width={20}
      height={20}
      fill='none'
      {...props}
      style={{ overflow: 'visible', ...props.style }}
    >
      <g ref={groupRef}>
        <circle cx={3.691} cy={10} r={0.537} fill='#FEFDFB' />
        <circle cx={10} cy={0.537} r={0.537} fill='#FEFDFB' />
        <circle cx={10} cy={6.846} r={0.537} fill='#FEFDFB' />
        <circle cx={10} cy={13.154} r={0.537} fill='#FEFDFB' />
        <circle cx={10} cy={3.692} r={0.537} fill='#FEFDFB' />
        <circle cx={10} cy={10} r={0.537} fill='#FEFDFB' />
        <circle cx={10} cy={16.308} r={0.537} fill='#FEFDFB' />
        <circle cx={10} cy={19.463} r={0.537} fill='#FEFDFB' />
        <circle cx={16.308} cy={10} r={0.537} fill='#FEFDFB' />
        <circle cx={0.537} cy={10} r={0.537} fill='#FEFDFB' />
        <circle cx={6.846} cy={10} r={0.537} fill='#FEFDFB' />
        <circle cx={13.154} cy={10} r={0.537} fill='#FEFDFB' />
        <circle cx={19.463} cy={10} r={0.537} fill='#FEFDFB' />
      </g>
    </svg>
  );
};

export default PlusSubmitIcon;
